export const queryKeys = {
  mcp: {
    // Per user: the API answers by who's asking, and logout keeps the cache for the next person to sign in.
    authorization: (requestId: string, userId: string) => ['mcp', 'authorization', requestId, userId] as const,
    canManage: (clientId: string, userId: string) => ['mcp', 'canManage', clientId, userId] as const,
    accessAll: ['mcp', 'access'] as const,
    access: (clientId: string, userId: string) => ['mcp', 'access', clientId, userId] as const,
    connectionsAll: ['mcp', 'connections'] as const,
    connections: (clientId: string, userId: string) => ['mcp', 'connections', clientId, userId] as const,
    supportConnections: (userId: string) => ['mcp', 'connections', 'server', 'support', userId] as const,
    myAccess: (userId: string) => ['mcp', 'myAccess', userId] as const,
    myAccessAll: ['mcp', 'myAccess'] as const,
    playbooks: (clientId: string) => ['mcp', 'playbooks', clientId] as const,
    playbookList: (clientId: string, userId: string) => ['mcp', 'playbooks', clientId, 'list', userId] as const,
    playbook: (clientId: string, id: string, userId: string) => ['mcp', 'playbooks', clientId, 'detail', id, userId] as const,
    firmProfile: (clientId: string, userId: string) => ['mcp', 'firmProfile', clientId, userId] as const,
    catalog: (clientId: string, userId: string) => ['mcp', 'catalog', clientId, userId] as const,
    catalogValues: (clientId: string, userId: string, dimension: string, search: string) =>
      ['mcp', 'catalog', clientId, userId, 'values', dimension, search] as const,
  },
  clients: {
    all: ['clients'] as const,
    list: (filters: {
      name?: string
      status?: 'Active' | 'Inactive' | 'all'
    }) =>
      ['clients', 'list', filters] as const,
    pbiGroups: () => ['clients', 'pbiGroups'] as const,
    detail: (clientId: string) => ['clients', 'detail', clientId] as const,
    logo: (clientId: string) => ['clients', 'logo', clientId] as const,
    featureAccess: (clientId: string) => ['clients', 'featureAccess', clientId] as const,
  },
  admin: {
    all: ['admin'] as const,
    users: (filters: { name?: string; email?: string; role?: string; status?: string }) =>
      ['admin', 'users', filters] as const,
  },
  platform: {
    all: ['platform'] as const,
    list: (filters: {
      clientId?: string
      name?: string
      companyName?: string
      type?: string
      status?: string
    }) =>
      ['platform', 'list', filters] as const,
    clientRefreshHistory: (clientId: string) => ['platform', 'clientRefreshHistory', clientId] as const,
  },
  fileUpload: {
    all: ['fileUpload'] as const,
    history: (filters: { clientId?: string; page?: number; pageSize?: number; fileName?: string; status?: string }) =>
      ['fileUpload', 'history', filters] as const,
    tables: (clientId?: string) => ['fileUpload', 'tables', clientId || ''] as const,
  },
  fileUploadRules: {
    all: ['fileUploadRules'] as const,
    list: () => ['fileUploadRules', 'list'] as const,
    detail: (tableName: string) => ['fileUploadRules', 'detail', tableName] as const,
    viewTemplates: () => ['fileUploadRules', 'viewTemplates'] as const,
  },
  users: {
    all: ['users'] as const,
    list: (filters: { name?: string; status?: string; clientId?: string }) =>
      ['users', 'list', filters] as const,
  },
  userGroups: {
    all: ['userGroups'] as const,
    list: (filters: { name?: string; status?: string }) =>
      ['userGroups', 'list', filters] as const,
    detail: (groupId: string) => ['userGroups', 'detail', groupId] as const,
    createInit: (clientId?: string) => ['userGroups', 'createInit', clientId || ''] as const,
  },
  reports: {
    all: ['reports'] as const,
    list: (filters: { clientId?: string; title?: string; status?: string }) => ['reports', 'list', filters] as const,
    reportBuilderList: (clientId: string, isTemplate: boolean, filters: { title?: string } = {}) =>
      ['reports', 'reportBuilderList', clientId, isTemplate, filters] as const,
    reportBuilderHomeList: (clientId: string, isTemplate: boolean) =>
      ['reports', 'reportBuilderHomeList', clientId, isTemplate] as const,
    reportBuilder: (reportBuilderId: string) => ['reports', 'reportBuilder', reportBuilderId] as const,
    reportBuilderOptions: (clientId: string, platformType: number) =>
      ['reports', 'reportBuilderOptions', clientId, platformType] as const,
    reportBuilderScheduleList: (clientId: string, id: string, isGroup = false) =>
      ['reports', 'reportBuilderScheduleList', clientId, id, isGroup] as const,
    reportBuilderSchedule: (reportBuilderScheduleId: string) =>
      ['reports', 'reportBuilderSchedule', reportBuilderScheduleId] as const,
    recipients: (clientId: string) => ['reports', 'recipients', clientId] as const,
    notificationOptionsList: (clientId?: string) => ['reports', 'notificationOptionsList', clientId || ''] as const,
    notificationList: (clientId: string, notificationOptionID: string) =>
      ['reports', 'notificationList', clientId, notificationOptionID] as const,
    notificationOptionOrganisations: (clientId: string, platformType: number) =>
      ['reports', 'notificationOptionOrganisations', clientId, platformType] as const,
    previewData: (clientId: string, reportBuilderFileId: string) =>
      ['reports', 'previewData', clientId, reportBuilderFileId] as const,
    downloadStatus: (clientId: string, reportBuilderFileId: string) =>
      ['reports', 'downloadStatus', clientId, reportBuilderFileId] as const,
  },
  advisory: {
    dashboards: () => ['advisory', 'dashboards'] as const,
    mapping: (filters: { name?: string; status?: string }) =>
      ['advisory', 'mapping', filters] as const,
    surveyForms: (filters: { name?: string; status?: string }) =>
      ['advisory', 'surveyForms', filters] as const,
    /** Every aiOrgs entry regardless of filters — for invalidating or polling the lot. */
    aiOrgsPrefix: () => ['advisory', 'aiOrgs'] as const,
    aiOrgs: (filters: {
      clientId?: string
      name?: string
      companyName?: string
      typeId?: number
    }) => [...queryKeys.advisory.aiOrgsPrefix(), filters] as const,
    aiOrgDetail: (orgId: string, clientId: string) => ['advisory', 'aiOrgDetail', orgId, clientId] as const,
    aiOrgInternalMemory: (orgId: string, clientId: string) =>
      ['advisory', 'aiOrgInternalMemory', orgId, clientId] as const,
    aiOrgReports: (orgId: string, clientId: string, pageIndex: number, pageSize: number) =>
      ['advisory', 'aiOrgReports', orgId, clientId, pageIndex, pageSize] as const,
    aiReport: (reportId: string, clientId: string) => ['advisory', 'aiReport', reportId, clientId] as const,
    aiAgenda: (reportId: string, clientId: string) => ['advisory', 'aiAgenda', reportId, clientId] as const,
    aiAgendaTemplates: (clientId: string) => ['advisory', 'aiAgendaTemplates', clientId] as const,
    aiThemeColors: (clientId: string) => ['advisory', 'aiThemeColors', clientId] as const,
    aiExports: (reportId: string, clientId: string) => ['advisory', 'aiExports', reportId, clientId] as const,
    consolidatedList: (clientId: string) => ['advisory', 'consolidatedList', clientId] as const,
    consolidated: (id: string, clientId: string) => ['advisory', 'consolidated', id, clientId] as const,
    popups: (clientId: string) => ['advisory', 'popups', clientId] as const,
    popupSettings: (clientId: string) => ['advisory', 'popupSettings', clientId] as const,
    feedbackStatus: (clientId: string) => ['advisory', 'feedbackStatus', clientId] as const,
    supportTickets: (clientId: string) => ['advisory', 'supportTickets', clientId] as const,
    // clientId is the admin's session context, not the scope — the console
    // spans clients. It stays in the key: it changes who the request is made as.
    supportTicketsAll: (
      clientId: string,
      filters: {
        status?: string
        area?: string
        search?: string
        clientFilter?: string
        skip?: number
        take?: number
      }
    ) => ['advisory', 'supportTicketsAll', clientId, filters] as const,
    supportTicketHistory: (ticketId: string, clientId: string) =>
      ['advisory', 'supportTicketHistory', ticketId, clientId] as const,
    supportTicketReplies: (ticketId: string, clientId: string) =>
      ['advisory', 'supportTicketReplies', ticketId, clientId] as const,
    // Kept apart from the admin key: same thread, but fetched as the raiser.
    supportTicketThread: (ticketId: string, clientId: string) =>
      ['advisory', 'supportTicketThread', ticketId, clientId] as const,
    // `clientId` is in the key because it is on the request, not because it
    // scopes the content.
    helpArticles: (clientId: string) => ['advisory', 'helpArticles', clientId] as const,
    media: () => ['advisory', 'media'] as const,
    voiceClone: (id: string) => ['advisory', 'voiceClone', id] as const,
  },
  surveyForm: {
    list: (clientId: string) => ['surveyForm', 'list', clientId] as const,
    questions: () => ['surveyForm', 'questions'] as const,
  },
  advisoryMapping: {
    all: ['advisoryMapping'] as const,
    list: (clientId: string) => ['advisoryMapping', 'list', clientId] as const,
    detail: (platformId: string, clientId: string) =>
      ['advisoryMapping', 'detail', platformId, clientId] as const,
  },
  adviceprep: {
    outstandingInvoice: (clientId: string) => ['adviceprep', 'outstanding-invoice', clientId] as const,
    liveSubscription: (clientId: string) => ['adviceprep', 'live-subscription', clientId] as const,
    /**
     * Kept apart from liveSubscription even though both read the same endpoint.
     * The trial allowance is read on page load, while the edit modal's check
     * runs when an admin opens it to change billing — sharing one entry would
     * hand the modal whatever the page cached up to a minute earlier.
     */
    trialState: (clientId: string) => ['adviceprep', 'trial-state', clientId] as const,
  },
  billing: {
    overview: (clientId: string) => ['billing', 'overview', clientId] as const,
    access: (clientId: string) => ['billing', 'access', clientId] as const,
  },
  profile: {
    detail: () => ['profile', 'detail'] as const,
    authenticator: () => ['profile', 'authenticator'] as const,
  },
  support: {
    prefill: (userId: string) => ['support', 'prefill', userId] as const,
  },
  clientTabs: {
    report: (clientId: string) => ['clientTabs', 'report', clientId] as const,
    surveyForm: (clientId: string) => ['clientTabs', 'surveyForm', clientId] as const,
  },
  projectStatus: {
    detail: (clientId: string) => ['projectStatus', 'detail', clientId] as const,
  },
  staff: {
    all: ['staff'] as const,
    list: (clientId: string, filters: { staffName?: string }) =>
      ['staff', 'list', clientId, filters] as const,
    clientUsers: (clientId: string) => ['staff', 'clientUsers', clientId] as const,
    details: (uuid: string) => ['staff', 'details', uuid] as const,
    data: (uuid: string) => ['staff', 'data', uuid] as const,
    monthlyBudget: (uuid: string) => ['staff', 'monthlyBudget', uuid] as const,
  },
  dashboard: {
    all: ['dashboard'] as const,
    list: (
      request: { scope: 'client' | 'user'; clientId: string; userId: string },
      filters: { name?: string; powerBIReportName?: string | null }
    ) => ['dashboard', 'list', request.scope, request.clientId, request.userId, filters] as const,
    detailInit: (request: { scope: 'client' | 'user'; clientId: string; userId: string }) =>
      ['dashboard', 'detailInit', request.scope, request.clientId, request.userId] as const,
    editInit: (reportId: string) => ['dashboard', 'editInit', reportId] as const,
    pbiReports: (clientId: string) => ['dashboard', 'pbiReports', clientId] as const,
    pbiPages: (reportId: string) => ['dashboard', 'pbiPages', reportId] as const,
    embed: (reportId: string) => ['dashboard', 'embed', reportId] as const,
    exportStatus: (reportId: string, exportId: string) =>
      ['dashboard', 'exportStatus', reportId, exportId] as const,
  },
  bas: {
    org: (orgId: string) => ['bas', 'org', orgId] as const,
    jobs: (orgId: string) => ['bas', 'org', orgId, 'jobs'] as const,
    openJobStatus: (reviewId: string) => ['bas', 'open-job-status', reviewId] as const,
    orgDetail: (orgId: string, clientId: string) => ['bas', 'org', orgId, 'detail', clientId] as const,
    accounts: (orgId: string, clientId: string, pageIndex: number, pageSize: number) =>
      ['bas', 'org', orgId, 'accounts', clientId, pageIndex, pageSize] as const,
    transactions: (orgId: string, reviewId: string, status?: string) =>
      ['bas', 'org', orgId, 'transactions', reviewId, status] as const,
    sourceTransactions: (
      orgId: string,
      reviewId: string,
      pageIndex: number,
      pageSize: number,
      sortField?: string,
      sortDirection?: string
    ) =>
      [
        'bas',
        'org',
        orgId,
        'source-transactions',
        reviewId,
        pageIndex,
        pageSize,
        sortField || '',
        sortDirection || '',
      ] as const,
    reviewedTransactions: (
      orgId: string,
      reviewId: string,
      pageIndex: number,
      pageSize: number,
      status?: string,
      sortField?: string,
      sortDirection?: string
    ) =>
      [
        'bas',
        'org',
        orgId,
        'reviewed-transactions',
        reviewId,
        pageIndex,
        pageSize,
        status || 'all',
        sortField || '',
        sortDirection || '',
      ] as const,
    reviewDetails: (reviewId: string, clientId: string) =>
      ['bas', 'review', reviewId, 'details', clientId] as const,
    reviewJobStatus: (reviewId: string, clientId: string) =>
      ['bas', 'review', reviewId, 'job-status', clientId] as const,
    rules: (clientId: string, orgId?: string) => ['bas', 'rules', clientId, orgId || 'global'] as const,
    rule: (clientId: string, ruleId: string) => ['bas', 'rule', clientId, ruleId] as const,
    organisations: (clientId: string) => ['bas', 'organisations', clientId] as const,
    emailTemplates: (clientId: string) => ['bas', 'email-templates', clientId] as const,
    validationErrors: (clientId: string, reviewId: string) =>
      ['bas', 'validation-errors', clientId, reviewId] as const,
    journals: (clientId: string, reviewId: string) => ['bas', 'journals', clientId, reviewId] as const,
    activeAccounts: (clientId: string, orgId: string) => ['bas', 'active-accounts', clientId, orgId] as const,
  },
}
